import { Formik, useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { ExclamationCircleFill } from 'react-bootstrap-icons';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { mergeUniqueProductFiles } from '../../../helpers/product-file-delete-helper';
import { IActionFormatter } from '../../../models/ICommon';
import {
  IProductFiles,
  IProductImages,
} from '../../../models/response/IProductResponse';
import { ITeplarVM } from '../../../models/response/ITeplarResponse';
import LoaderButton from '../../../shared-components/button/loader-button';
import { RemoveIcon } from '../../../shared-components/svgs/svgs';
import ProductStepper from './product-stepper';
import { formatMessage } from '../../../translations/format-message';
import ProcessTabContent from '../components/tab-view/process-tab-content';
import ProcessTabView from '../components/tab-view/process-tab-view';
import './step-four.css';

interface IProps {
  data: any;
  id: number;
  isLoading: boolean;
  // teplarDetailsList: ITeplarVM[];
  onModalClosed: () => void;
  next: (newData: any, final?: boolean) => void;
  handleSaveAndNext: (newData: any, final?: boolean) => void;
  prev: (newData: any) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  setCloseModal: (isClose: boolean) => void;
}

const StepFour = (props: IProps) => {
  const showAttentionToast = (message: string) =>
    toast(message, {
      icon: (
        <ExclamationCircleFill color="#ffcc00" size={18} />
      ),
    });

  const { productStore, teplarPrintStore } = useStore();
  const {
    addProductFileState,
    deleteProductFileState,
    allProductImages,
    resetDeleteProductFileState,
    resetAddProductFileState,
    resetProductImages,
    GetAllProductService,
    resetCopiedImagesUrl,
    GetDimensionByProcessIdService,
  } = productStore;
  const productImages: IProductImages[] = allProductImages;
  const [maxLength, setMaxLength] = useState(0);
  const [selectedProcessId, setSelectedProcessId] = useState<number>(
    props.data.Process.filter(
      (pro: { ProcessId: number }) =>
        pro.ProcessId !== -1 &&
        pro.ProcessId !== -2 &&
        pro.ProcessId !== -3 &&
        pro.ProcessId !== -4
    )[0]?.ProcessId || 0
  );

  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState<boolean>(false);
  const [pendingUpdateValues, setPendingUpdateValues] = useState<any>(null);

  const hasAnyProcessCorrectionActive = (processList: any[]): boolean => {
    if (!processList || !Array.isArray(processList)) return false;
    const realProcesses = processList.filter(
      (p: any) => p.ProcessId !== -1 && p.ProcessId !== -2 && p.ProcessId !== -3 && p.ProcessId !== -4
    );
    if (realProcesses.length === 0) return false;

    return realProcesses.some((proc: any) => {
      return Boolean(
        (proc?.CorrectionRequest && typeof proc?.CorrectionRequest === 'object' && !proc?.CorrectionRequest?.IsCompleted) ||
        proc?.CorrectionRequest === true ||
        (proc?.HasCorrectionRequest && !proc?.IsCorrectionCompleted)
      );
    });
  };

  const handleConfirmUpdateWithoutCorrection = () => {
    setShowUpdateConfirmation(false);
    if (pendingUpdateValues) {
      props.next({ ...pendingUpdateValues }, true);
      setPendingUpdateValues(null);
    }
  };

  const handleCancelUpdateWithoutCorrection = () => {
    setShowUpdateConfirmation(false);
    setPendingUpdateValues(null);
  };

  let tabRef = useRef<HTMLDivElement>(null);
  const hasLoggedStepFourHeadings = useRef(false);

  const hasPrintProcess = (processList: any[] = []) =>
    processList.some((process: any) => Number(process?.Type) === 2);

  const chunkTeplarItems = (items: string[], layout: number) => {
    const chunkSize = Math.max(Number(layout || 0), 1);
    const chunks: string[][] = [];

    for (let i = 0; i < items.length; i += chunkSize) {
      chunks.push(items.slice(i, i + chunkSize));
    }

    return chunks;
  };

  const normalizeTeplarsForSave = (
    teplars: any[] = [],
    removeItemSpaces = false
  ) =>
    teplars
      .flatMap((item: any, index: number) => {
        const itemsToProcess = Array.isArray(item.Items)
          ? item.Items
          : (item.Items || '').split(',');
        const filteredItems = itemsToProcess
          .map((s: string) =>
            typeof s === 'string'
              ? removeItemSpaces
                ? s.replaceAll(' ', '')
                : s.trim()
              : ''
          )
          .filter((value: string) => value !== '');
        const teplarPayload = { ...item };
        delete teplarPayload.rowSource;
        delete teplarPayload.originalLayout;
        delete teplarPayload.baseLayout;
        const baseLayout = Number(
          item.baseLayout || item.originalLayout || item.Layout || 0
        );
        const effectiveLayout = Math.max(baseLayout, 1);

        return chunkTeplarItems(filteredItems, effectiveLayout).map(
          (chunk, chunkIndex) => ({
            ...teplarPayload,
            Id: chunkIndex === 0 ? Number(item?.Id || 0) : 0,
            Order: Number(item?.Order || index + 1) + chunkIndex,
            Items: chunk.join(','),
            Layout: Math.max(effectiveLayout, chunk.length, 1),
            Position: item?.Position || '',
            Remarks: item?.Remarks || '',
          })
        );
      })
      .filter((item: any) => item.Items !== '')
      .map((item: any, index: number) => ({
        ...item,
        Order: index + 1,
      }));

  const ledRowHasData = (row: any): boolean => {
    const hasValue = (val: any) => val !== '' && val !== null && val !== undefined;
    return (
      String(row?.LEDId || '').trim() !== '' ||
      hasValue(row?.CenterValue) ||
      hasValue(row?.MaxValue) ||
      hasValue(row?.MinValue)
    );
  };

  const getProductTeplarsForSave = (
    sanitizedValues: any,
    removeItemSpaces = false
  ) => {
    const formTeplars = normalizeTeplarsForSave(
      sanitizedValues.ProductTeplars || [],
      removeItemSpaces
    );
    if (formTeplars.length > 0) return formTeplars;
    if (props.id > 0) return [];
    if (!hasPrintProcess(sanitizedValues.Process || [])) return [];
    if (
      String(teplarPrintStore.teplarDetailsProductId || '').trim() !==
      String(sanitizedValues?.ProductName || '').trim()
    )
      return [];

    // If the user already imported/edited Teplars in Step 4 and cleared them,
    // keep the form as the source of truth. Otherwise support the pre-import
    // flow where matching CSV rows exist in the store before Step 4 is opened.
    if (sanitizedValues.IsImport) return [];

    return normalizeTeplarsForSave(
      teplarPrintStore.teplarDetailsList || [],
      removeItemSpaces
    );
  };

  const normalizedStepFourData = useMemo(() => {
    const source = props.data || {};
    const sourceProcess = Array.isArray(source?.Process) ? source.Process : [];
    const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';

    const commonFromProcess =
      sourceProcess.find((p: any) => Number(p?.ProcessId) === -3)?.fileIndices || [];
    const commonFromRoot = Array.isArray(source?.CommonProcess)
      ? source.CommonProcess
      : [];
    const commonFromRealProcesses: any[] = [];
    const commonKeys = new Set<string>();

    sourceProcess.forEach((process: any) => {
      if (Number(process?.ProcessId) <= 0) return;

      (process?.fileIndices || []).forEach((tab: any) => {
        if (!(isTrue(tab?.IsDefault) || isTrue(tab?.isDefault))) return;

        const heading = String(tab?.FileDetail?.[0]?.Heading || '')
          .trim()
          .toLowerCase();
        const path = String(
          tab?.FileDetail?.[0]?.S3FilePath ||
          tab?.FileDetail?.[0]?.FilePath ||
          ''
        ).trim();
        const key = heading || path || String(tab?.TabIndex || '');

        if (!key || commonKeys.has(key)) return;
        commonKeys.add(key);
        commonFromRealProcesses.push(tab);
      });
    });

    // Cap to the same limit enforced by the UI (commonTabLimit = 5).
    // This prevents a stale 6th entry left by a prior edit (e.g. on a copied product)
    // from being picked up and distributed to every process's fileIndices.
    const MAX_COMMON_TABS = 5;
    const commonSeed = (
      commonFromProcess.length > 0
        ? commonFromProcess
        : commonFromRoot.length > 0
          ? commonFromRoot
          : commonFromRealProcesses
    ).slice(0, MAX_COMMON_TABS);

    const dimensionTemplateTabs =
      sourceProcess.find((p: any) => Number(p?.ProcessId) === -4)?.fileIndices || [];
    const firstDimensionTemplate = dimensionTemplateTabs.find((fi: any) =>
      isTrue(fi?.IsDimension) || isTrue(fi?.isDimension)
    );

    const normalizedProcess = sourceProcess.map((process: any) => {
      const processId = Number(process?.ProcessId);
      if (processId === -1 || processId === -2 || processId === -3 || processId === -4) {
        return {
          ...process,
          fileIndices: Array.isArray(process?.fileIndices) ? process.fileIndices : [],
        };
      }

      const existing = Array.isArray(process?.fileIndices) ? process.fileIndices : [];
      const usedExIndices = new Set<number>();
      const usedFileIds = new Set<number>();
      const allExistingFiles = existing.flatMap((fi: any) => fi.FileDetail || []);

      const commonTabs = commonSeed.map((common: any) => {
        const commonHeading = common?.FileDetail?.[0]?.Heading;
        
        // Match by heading first because common tabs are renamed/reordered by the
        // user. File identity can overlap across C-tabs after copy-product flows.
        let matchedIndex = existing.findIndex((ex: any, idx: number) => {
          if (usedExIndices.has(idx)) return false;
          if (!(isTrue(ex?.IsDefault) || isTrue(ex?.isDefault))) return false;
          
          const exHeading = String(ex?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
          const seedHeading = String(commonHeading || '').trim().toLowerCase();
          return Boolean(exHeading && seedHeading && exHeading === seedHeading);
        });

        if (matchedIndex === -1) {
          matchedIndex = existing.findIndex((ex: any, idx: number) => {
            if (usedExIndices.has(idx)) return false;
            if (!(isTrue(ex?.IsDefault) || isTrue(ex?.isDefault))) return false;

          return ex?.FileDetail?.some((ef: any) =>
              common?.FileDetail?.some((cf: any) => {
                const efId = Number(ef.FileId || 0);
                const cfId = Number(cf.FileId || 0);
                if (efId > 0 && cfId > 0 && efId === cfId) return true;

                const efPath = ef.FilePath || ef.S3FilePath;
                const cfPath = cf.FilePath || cf.S3FilePath;
                return efPath && cfPath && efPath === cfPath;
              })
            );
          });
        }

        // Last fallback: position, but only when neither side has a heading.
        if (matchedIndex === -1) {
          const commonIdx = commonSeed.indexOf(common);
          const existingCommonTabs = existing.filter((ex: any) => isTrue(ex?.IsDefault) || isTrue(ex?.isDefault));
          const matchingExTab = existingCommonTabs[commonIdx];
          const seedHeading = String(commonHeading || '').trim();
          const matchingHeading = String(matchingExTab?.FileDetail?.[0]?.Heading || '').trim();
          if (matchingExTab && !seedHeading && !matchingHeading) {
            const idxInExisting = existing.indexOf(matchingExTab);
            if (idxInExisting !== -1 && !usedExIndices.has(idxInExisting)) {
              matchedIndex = idxInExisting;
            }
          }
        }

        let matched = undefined;
        if (matchedIndex !== -1) {
          matched = existing[matchedIndex];
          usedExIndices.add(matchedIndex);
        }
        return {
          ...common,
          IsDefault: true,
          IsDimension: false,
          CheckItems: Array.isArray(matched?.CheckItems) ? matched.CheckItems : [],
          FileDetail: Array.isArray(common?.FileDetail)
            ? common.FileDetail.map((fd: any) => {
              // New Common files must stay FileId 0; matching by same name/path can reuse an old image id.
              if (fd?.IsNew || Number(fd?.FileId || 0) <= 0) {
                return { ...fd, FileId: 0 };
              }

              const matchedFile = allExistingFiles.find(
                (ef: any) => {
                  const fileId = Number(ef.FileId || 0);
                  if (fileId > 0 && usedFileIds.has(fileId)) return false;
                  return (
                    (fd.FilePath && ef.FilePath && fd.FilePath === ef.FilePath) ||
                    (fd.S3FilePath && ef.S3FilePath && fd.S3FilePath === ef.S3FilePath)
                  );
                }
              );
              const matchedFileId = Number(matchedFile?.FileId || 0);
              if (matchedFileId > 0) {
                usedFileIds.add(matchedFileId);
              }
              return {
                ...fd,
                FileId: matchedFileId
              };
            })
            : common?.FileDetail,
        };
      });

      const existingDimensionTabs = existing.filter(
        (fi: any) => isTrue(fi?.IsDimension) || isTrue(fi?.isDimension)
      );
      const dimensionTabs =
        existingDimensionTabs.length > 0
          ? existingDimensionTabs
          : firstDimensionTemplate
            ? [{ ...firstDimensionTemplate, IsDimension: true, IsDefault: false }]
            : [];

      const regularTabs = existing.filter(
        (fi: any) =>
          !(isTrue(fi?.IsDefault) || isTrue(fi?.isDefault)) &&
          !(isTrue(fi?.IsDimension) || isTrue(fi?.isDimension))
      );

      const orderedTabs = [...commonTabs, ...dimensionTabs, ...regularTabs].map(
        (tab: any, idx: number) => ({
          ...tab,
          TabIndex: idx + 1,
        })
      );

      return {
        ...process,
        fileIndices: orderedTabs,
      };
    });

    return {
      ...source,
      Process: normalizedProcess,
      CommonProcess: commonSeed,
      ProductLEDs:
        Array.isArray(source?.ProductLEDs)
          ? source.ProductLEDs
          : Array.isArray(source?.ProductLeds)
          ? source.ProductLeds
          : Array.isArray(productStore?.product?.ProductLEDs) && productStore.product.ProductLEDs.length > 0
          ? productStore.product.ProductLEDs
          : Array.isArray(productStore?.product?.ProductLeds) && productStore.product.ProductLeds.length > 0
          ? productStore.product.ProductLeds
          : [],
    };
  }, [props.data, productStore?.product?.ProductLEDs, productStore?.product?.ProductLeds]);

  useEffect(() => {
    if (addProductFileState.success) {
      toast.success(formatMessage('added_success'));
      resetAddProductFileState();
    }
  }, [addProductFileState.success]);

  useEffect(() => {
    if (addProductFileState.error) {
      toast.error(formatMessage(addProductFileState.error));
      resetAddProductFileState();
    }
  }, [addProductFileState.error]);

  useEffect(() => {
    if (deleteProductFileState.success) {
      toast.success(formatMessage('deleted_success'));
      resetDeleteProductFileState();
    }
  }, [deleteProductFileState.success]);

  useEffect(() => {
    if (deleteProductFileState.error) {
      toast.error(formatMessage(deleteProductFileState.error));
      resetDeleteProductFileState();
    }
  }, [deleteProductFileState.error]);

  useEffect(() => {
    if (deleteProductFileState.error) {
      toast.error(formatMessage(deleteProductFileState.error));
      resetDeleteProductFileState();
    }
  }, [deleteProductFileState.error]);

  useEffect(() => {
    GetAllProductService(Number(getTenantIdFromLocalStorage()));
    return () => resetCopiedImagesUrl();
  }, []);

  useEffect(() => {
    if (hasLoggedStepFourHeadings.current) return;
    if (props.id <= 0 || !props.data?.Process?.length) return;

    const apiHeadingFileIds = props.data.Process.flatMap((process: any) =>
      (process?.fileIndices || []).flatMap((tab: any) =>
        (tab?.FileDetail || [])
          .filter((file: any) => Number(file?.FileId || 0) > 0)
          .map((file: any) => ({
            Heading: file?.Heading || '',
            FileId: file?.FileId,
          }))
      )
    );

    const headingFileIds = Array.from(
      new Map(
        apiHeadingFileIds.map((file: any) => [Number(file.FileId), file])
      ).values()
    );

    if (headingFileIds.length === 0) return;

    // console.log('Step 4 GetById Heading/FileId List', headingFileIds);
    hasLoggedStepFourHeadings.current = true;
  }, [props.data, props.id]);

  const hasHeaderWithoutCheckItems = (values: any) => {
    const validationMap = values?.CheckItemHeaderValidationMap ?? {};
    if (!values?.Process || !Array.isArray(values.Process)) return false;

    for (const process of values.Process) {
      if (!process?.fileIndices || !Array.isArray(process.fileIndices)) continue;
      const processId = Number(process?.ProcessId);
      for (const fileIndex of process.fileIndices) {
        const tabIndex = Number(fileIndex?.TabIndex);
        if (processId > 0 && tabIndex > 0) {
          const validationKey = `${processId}_${tabIndex}`;
          if (validationMap?.[validationKey]) return true;
        }
      }
    }
    return false;
  };

  const hasDuplicateHeaderNames = (values: any) => {
    const validationMap = values?.CheckItemDuplicateHeaderValidationMap ?? {};
    if (!values?.Process || !Array.isArray(values.Process)) return false;

    for (const process of values.Process) {
      if (!process?.fileIndices || !Array.isArray(process.fileIndices)) continue;
      const processId = Number(process?.ProcessId);
      for (const fileIndex of process.fileIndices) {
        const tabIndex = Number(fileIndex?.TabIndex);
        if (processId > 0 && tabIndex > 0) {
          const validationKey = `${processId}_${tabIndex}`;
          if (validationMap?.[validationKey]) return true;
        }
      }
    }
    return false;
  };

  const hasEmptyCheckItemContent = (values: any) => {
    if (!values?.Process || !Array.isArray(values.Process)) return false;

    for (const process of values.Process) {
      if (!process?.fileIndices || !Array.isArray(process.fileIndices)) continue;

      for (const fileIndex of process.fileIndices) {
        if (!fileIndex?.CheckItems || !Array.isArray(fileIndex.CheckItems)) continue;

        for (const item of fileIndex.CheckItems) {
          const messages =
            item?.CheckItems && Array.isArray(item.CheckItems)
              ? item.CheckItems
              : item?.CheckItemMessage && Array.isArray(item.CheckItemMessage)
                ? item.CheckItemMessage
                : [item];

          for (const msg of messages) {
            const text = String(
              msg?.CheckMessage ?? msg?.text ?? msg?.CheckItemMessage ?? ''
            ).trim();
            if (text === '') return true;
          }
        }
      }
    }

    return false;
  };

  const hasDuplicateCheckItemNames = (values: any) => {
    if (!values?.Process || !Array.isArray(values.Process)) return false;

    for (const process of values.Process) {
      if (!process?.fileIndices || !Array.isArray(process.fileIndices)) continue;

      for (const fileIndex of process.fileIndices) {
        if (!fileIndex?.CheckItems || !Array.isArray(fileIndex.CheckItems)) continue;

        const itemsByHeader: { [key: string]: Set<string> } = {};

        for (const item of fileIndex.CheckItems) {
          const header = String(item?.Title || item?.HeaderTitle || '').trim();
          if (!itemsByHeader[header]) {
            itemsByHeader[header] = new Set();
          }

          const messages =
            item?.CheckItems && Array.isArray(item.CheckItems)
              ? item.CheckItems
              : item?.CheckItemMessage && Array.isArray(item.CheckItemMessage)
                ? item.CheckItemMessage
                : [item];

          for (const msg of messages) {
            const text = String(
              msg?.CheckMessage ?? msg?.text ?? msg?.CheckItemMessage ?? ''
            )
              .trim()
              .toLowerCase();

            if (!text) continue;
            if (itemsByHeader[header].has(text)) return true;
            itemsByHeader[header].add(text);
          }
        }
      }
    }

    return false;
  };

  const sanitizeDuplicateCheckItems = (values: any) => {
    if (!values?.Process || !Array.isArray(values.Process)) return values;

    const clonedValues = {
      ...values,
      Process: values.Process.map((process: any) => {
        if (!process?.fileIndices || !Array.isArray(process.fileIndices)) {
          return process;
        }

        return {
          ...process,
          fileIndices: process.fileIndices.map((fileIndex: any) => {
            if (!fileIndex?.CheckItems || !Array.isArray(fileIndex.CheckItems)) {
              return fileIndex;
            }

            const itemsByHeader: { [key: string]: Set<string> } = {};
            const sanitizedCheckItems: any[] = [];

            for (const item of fileIndex.CheckItems) {
              const header = String(item?.Title || item?.HeaderTitle || '').trim();
              if (!itemsByHeader[header]) {
                itemsByHeader[header] = new Set();
              }

              const hasNestedCheckItems = item?.CheckItems && Array.isArray(item.CheckItems);
              const hasNestedCheckItemMessage =
                item?.CheckItemMessage && Array.isArray(item.CheckItemMessage);
              const messages = hasNestedCheckItems
                ? item.CheckItems
                : hasNestedCheckItemMessage
                  ? item.CheckItemMessage
                  : [item];

              const uniqueMessages: any[] = [];

              for (const msg of messages) {
                const text = String(
                  msg?.CheckMessage ?? msg?.text ?? msg?.CheckItemMessage ?? ''
                ).trim();
                const normalized = text.toLowerCase();

                if (!normalized) {
                  uniqueMessages.push(msg);
                  continue;
                }

                if (itemsByHeader[header].has(normalized)) {
                  continue;
                }

                itemsByHeader[header].add(normalized);
                uniqueMessages.push(msg);
              }

              if (hasNestedCheckItems || hasNestedCheckItemMessage) {
                if (hasNestedCheckItems) {
                  sanitizedCheckItems.push({
                    ...item,
                    CheckItems: uniqueMessages,
                  });
                } else {
                  sanitizedCheckItems.push({
                    ...item,
                    CheckItemMessage: uniqueMessages,
                  });
                }
              } else if (uniqueMessages.length > 0) {
                sanitizedCheckItems.push(uniqueMessages[0]);
              }
            }

            return {
              ...fileIndex,
              CheckItems: sanitizedCheckItems,
            };
          }),
        };
      }),
    };

    return clonedValues;
  };

  const hasDuplicateTabHeadings = (values: any) => {
    if (!values?.Process || !Array.isArray(values.Process)) return false;
    const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';

    for (const process of values.Process) {
      if (!process?.fileIndices || !Array.isArray(process.fileIndices)) continue;
      const commonHeadings: string[] = [];
      const processHeadings: string[] = [];

      for (const fi of process.fileIndices) {
        const isCommon = isTrue(fi.IsDefault) || isTrue(fi.isDefault);
        const isDimension = isTrue(fi.IsDimension) || isTrue(fi.isDimension);
        if (isDimension) continue;

        const heading = String(fi?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
        if (!heading) continue;

        if (isCommon) {
          commonHeadings.push(heading);
        } else {
          processHeadings.push(heading);
        }
      }

      if (new Set(commonHeadings).size !== commonHeadings.length) return true;
      if (new Set(processHeadings).size !== processHeadings.length) return true;
    }
    return false;
  };

  const hasEmptyHeadingInProcessTabs = (values: any) => {
    if (!values?.Process || !Array.isArray(values.Process)) return false;
    const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';

    for (const process of values.Process) {
      const processId = Number(process?.ProcessId);
      if (processId <= 0) continue; // skip virtual processes (-1, -2, -3, -4)
      if (!process?.fileIndices || !Array.isArray(process.fileIndices)) continue;

      for (const fi of process.fileIndices) {
        const isCommon = isTrue(fi?.IsDefault) || isTrue(fi?.isDefault);
        const isDimension = isTrue(fi?.IsDimension) || isTrue(fi?.isDimension);
        if (isCommon || isDimension) continue;

        const heading = String(fi?.FileDetail?.[0]?.Heading || '').trim();
        const hasFile = (fi?.FileDetail || []).some((fd: any) => {
          const fileId = Number(fd?.FileId || 0);
          return Boolean(
            (fd?.S3FilePath && String(fd.S3FilePath).trim() !== '') ||
            (fd?.FilePath && String(fd.FilePath).trim() !== '') ||
            fileId > 0
          );
        });

        if (hasFile && heading === '') return true;
      }
    }
    return false;
  };

  const handleSubmit = (values: any) => {
    if (hasEmptyHeadingInProcessTabs(values)) {
      toast.error(formatMessage('please_enter_heading'));
      return;
    }
    if (hasDuplicateTabHeadings(values)) {
      toast.error(formatMessage('duplicate_found_tabs'));
      return;
    }
    if (hasDuplicateCheckItemNames(values)) {
      showAttentionToast(formatMessage('duplicate_item_in_header'));
      return;
    }

    const sanitizedValues = sanitizeDuplicateCheckItems(values);
    if (hasEmptyCheckItemContent(sanitizedValues)) {
      toast.error(formatMessage('please_enter_check_content'));
      return;
    }
    if (hasHeaderWithoutCheckItems(sanitizedValues)) {
      toast.error(formatMessage('header_requires_check_item'));
      return;
    }
    if (hasDuplicateHeaderNames(sanitizedValues)) {
      showAttentionToast(formatMessage('duplicate_header_names_not_allowed'));
      return;
    }

    const filterTeplars = getProductTeplarsForSave(sanitizedValues);
    const {
      CheckItemHeaderValidationMap,
      CheckItemDuplicateHeaderValidationMap,
      ...valuesWithoutHeaderValidation
    } = sanitizedValues;

    const filterLEDs = Array.isArray(sanitizedValues.ProductLEDs)
      ? sanitizedValues.ProductLEDs
          .filter(ledRowHasData)
          .map((row: any) => ({
            Id: Number(row.Id || 0),
            LEDId: String(row.LEDId || '').trim(),
            CenterValue: Number(row.CenterValue || 0),
            MaxValue: Number(row.MaxValue || 0),
            MinValue: Number(row.MinValue || 0),
          }))
      : [];

    let finalValues = {
      ...valuesWithoutHeaderValidation,
      ProductFiles: JSON.parse(localStorage.getItem('images')) || [],
      ProductTeplars: [...filterTeplars],
      ProductLEDs: filterLEDs,
      IsImport: filterTeplars.length > 0 ? true : !!sanitizedValues.IsImport,
    };

    if (props.id > 0 && hasAnyProcessCorrectionActive(sanitizedValues.Process)) {
      setPendingUpdateValues(finalValues);
      setShowUpdateConfirmation(true);
      return;
    }

    props.next({ ...finalValues }, true);
  };

  const handleSubmit1 = (values: any, isNotCreateNew: boolean = true) => {
    if (hasEmptyHeadingInProcessTabs(values)) {
      toast.error(formatMessage('please_enter_heading'));
      return;
    }
    if (hasDuplicateTabHeadings(values)) {
      toast.error(formatMessage('duplicate_found_tabs'));
      return;
    }
    if (hasDuplicateCheckItemNames(values)) {
      showAttentionToast(formatMessage('duplicate_item_in_header'));
      return;
    }

    const sanitizedValues = sanitizeDuplicateCheckItems(values);
    if (hasEmptyCheckItemContent(sanitizedValues)) {
      toast.error(formatMessage('please_enter_check_content'));
      return;
    }
    if (hasHeaderWithoutCheckItems(sanitizedValues)) {
      toast.error(formatMessage('header_requires_check_item'));
      return;
    }
    if (hasDuplicateHeaderNames(sanitizedValues)) {
      showAttentionToast(formatMessage('duplicate_header_names_not_allowed'));
      return;
    }

    const filterTeplars = getProductTeplarsForSave(sanitizedValues, true);

    const {
      CheckItemHeaderValidationMap,
      CheckItemDuplicateHeaderValidationMap,
      ...valuesWithoutHeaderValidation
    } = sanitizedValues;

    const filterLEDs = Array.isArray(sanitizedValues.ProductLEDs)
      ? sanitizedValues.ProductLEDs
          .filter(ledRowHasData)
          .map((row: any) => ({
            Id: Number(row.Id || 0),
            LEDId: String(row.LEDId || '').trim(),
            CenterValue: Number(row.CenterValue || 0),
            MaxValue: Number(row.MaxValue || 0),
            MinValue: Number(row.MinValue || 0),
          }))
      : [];

    let finalValues = {
      ...valuesWithoutHeaderValidation,
      ProductFiles: JSON.parse(localStorage.getItem('images')) || [],
      ProductTeplars: [...filterTeplars],
      ProductLEDs: filterLEDs,
      IsImport: filterTeplars.length > 0 ? true : !!sanitizedValues.IsImport,
    };

    props.handleSaveAndNext({ ...finalValues }, true);
    props.setCloseModal(false);
  };

  const DimensionOrderFetcher = () => {
    const { values, setFieldValue } = useFormikContext<any>();
    const { productStore } = useStore();

    useEffect(() => {
      if (values?.Process?.length > 0) {
        const processIds = values.Process.filter((p: any) => p.ProcessId > 0).map(
          (p: any) => p.ProcessId
        );
        if (processIds.length > 0) {
          productStore.GetDimensionByProcessIdService(
            processIds,
            setFieldValue,
            values
          );
        }
      }
    }, []);

    return null;
  };

  const FormObserver: React.FC = () => {
    return null;
  };

  const DeletedFilesPayloadLogger = () => {
    const { values } = useFormikContext<any>();
    const hasLoggedRef = useRef(false);

    useEffect(() => {
      const uniqueDeletedFiles = mergeUniqueProductFiles(
        values?.NewDeletedFiles || [],
        values?.OldDeletedFiles || []
      );

      const deletedFileIds = Array.from(
        new Set(
          uniqueDeletedFiles
            .map((file: any) => Number(file?.FileId || 0))
            .filter((fileId: number) => fileId > 0)
        )
      );

      const deletedFilePaths = Array.from(
        new Set(
          uniqueDeletedFiles
            .map((file: any) => file?.FilePath || file?.S3FilePath)
            .filter(Boolean)
        )
      );

      if (!hasLoggedRef.current && deletedFileIds.length === 0 && deletedFilePaths.length === 0) {
        return;
      }

      // console.log('Step 4 Pending Delete Payload', {
      //   FileId: deletedFileIds,
      //   FilePath: deletedFilePaths,
      //   TotalFiles: uniqueDeletedFiles.length,
      //   Files: uniqueDeletedFiles.map((file: any) => ({
      //     FileId: Number(file?.FileId || 0),
      //     Heading: file?.Heading || '',
      //     FileName: file?.FileName || '',
      //     FilePath: file?.FilePath || file?.S3FilePath || '',
      //   })),
      // });

      hasLoggedRef.current = true;
    }, [values?.NewDeletedFiles, values?.OldDeletedFiles]);

    return null;
  };

  const StepFourPayloadLogger = () => {
    const { values } = useFormikContext<any>();

    useEffect(() => {
      const processList = Array.isArray(values?.Process) ? values.Process : [];
      const liveCommonFiles = processList.flatMap((process: any) =>
        (process?.fileIndices || [])
          .filter((tab: any) => tab?.IsDefault || tab?.isDefault)
          .flatMap((tab: any) => tab?.FileDetail || [])
      );

      const uniqueCommonFiles = mergeUniqueProductFiles(liveCommonFiles);
      const newCommonFiles = uniqueCommonFiles.filter(
        (file: any) => Number(file?.FileId || 0) <= 0
      );

      if (uniqueCommonFiles.length > 0) {
        // console.log('Step 4 Live Common Image Payload', {
        //   FileId: uniqueCommonFiles
        //     .map((file: any) => Number(file?.FileId || 0))
        //     .filter((fileId: number) => fileId > 0),
        //   TotalFiles: uniqueCommonFiles.length,
        //   NewFilesCount: newCommonFiles.length,
        //   Files: uniqueCommonFiles.map((file: any) => ({
        //     FileId: Number(file?.FileId || 0),
        //     Heading: file?.Heading || '',
        //     FileName: file?.FileName || '',
        //     FileType: file?.FileType || '',
        //     FilePath: file?.FilePath || file?.S3FilePath || '',
        //     IsNew: Boolean(file?.IsNew || Number(file?.FileId || 0) <= 0),
        //   })),
        // });
      }

      if (newCommonFiles.length > 0) {
        // console.log('Step 4 New Common Files Going In Save API', {
        //   TotalFiles: newCommonFiles.length,
        //   Files: newCommonFiles.map((file: any) => ({
        //     FileId: Number(file?.FileId || 0),
        //     Heading: file?.Heading || '',
        //     FileName: file?.FileName || '',
        //     FileType: file?.FileType || '',
        //     FilePath: file?.FilePath || file?.S3FilePath || '',
        //   })),
        // });
      }
    }, [values?.Process]);

    return null;
  };

  return (
    <>
      <Formik
        initialValues={{ ...normalizedStepFourData, SelectedCopyProductImage: -1 }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        enableReinitialize
        validate={(values) => {
          const errors: any = {};
          // const isHeadingEmpty = heading.length === 0;
          // const isImageEmpty =
          //   !fileInputRef.current || fileInputRef.current.value.length === 0;
          // const isCopyImageEmpty =
          //   !selectedImage ||
          //   !selectedImage.Heading ||
          //   selectedImage.Heading.length === 0;

          // if (isHeadingEmpty && isImageEmpty && isCopyImageEmpty) return;

          // if (isHeadingEmpty && (isImageEmpty || isCopyImageEmpty)) {
          //   const errorMsg = formatMessage('upload_image_or_enter_heading');
          //   errors.heading = errorMsg;
          //   errors.productImages = errorMsg;
          // }

          // if (!isHeadingEmpty) return;

          return errors;
        }}
      >
        {({
          values,
          isValid,
          handleSubmit,
          setFieldValue,
          errors,
          resetForm,
        }) => {
          return (
            <Form
              id="formBody"
              onSubmit={handleSubmit}
            >
              <DimensionOrderFetcher />
              <DeletedFilesPayloadLogger />
              <StepFourPayloadLogger />
              <FormObserver />
              <Modal.Body className="overflow-hidden">
                <ProductStepper currentStep={4} />
                <div className="row">
                  <div className="col-12">
                    <div className="row  px-3 mb-2">
                      <div className="col-6">
                        <label>
                          <FormattedMessage id="product_id" />
                        </label>
                        <input
                          type="text"
                          value={props.data.ProductName}
                          placeholder="-"
                          className="form-control"
                          disabled
                        />
                      </div>
                      <div className="col-6">
                        <label>
                          <FormattedMessage id="katashiki" />
                        </label>
                        <input
                          type="text"
                          value={props.data.Katashiki}
                          placeholder="-"
                          className="form-control"
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-product-master">
                    <ProcessTabView
                      values={values}
                      setSelectedProcessId={setSelectedProcessId}
                      selectedProcessId={selectedProcessId}
                    />
                    <ProcessTabContent
                      id={props.id}
                      selectedProcessId={selectedProcessId}
                      setSelectedProcessId={setSelectedProcessId}
                    />
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className="d-flex justify-content-center">
                  <div className="row tab-action g-0">
                    <div className="col-12">
                      <Button
                        data-testid="back"
                        variant="light-danger"
                        onClick={() => {
                          if (props.id > 0) {
                            if (isValid) props.prev(values);
                          } else {
                            props.prev(values);
                          }
                          resetProductImages();
                        }}
                        disabled={props.isLoading}
                      >
                        <FormattedMessage id="back" />
                      </Button>
                      <LoaderButton
                        type="submit"
                        id="SaveTenant"
                        // isLoading={props.isLoading}
                        text={props.id > 0 ? 'update' : 'save'}
                      />

                      {/* {props.id < 0 && (
                        <LoaderButton
                          type="button"
                          id="SaveTenant"
                          onClick={(e) => {
                            handleSubmit1(props.data, false);
                            resetForm();
                            props.setCurrentStep(0);
                          }}
                          isLoading={props.isLoading}
                          text={props.id > 0 ? 'update' : 'save_create_new'}
                        />
                      )} */}
                    </div>
                  </div>
                </div>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>

      {showUpdateConfirmation && (
        <Modal
          show={true}
          centered
          onHide={handleCancelUpdateWithoutCorrection}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {formatMessage('confirm_update_title')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            <p className="mb-0 fs-6">
              {formatMessage('message_update_without_correction_confirm')}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light-danger"
              onClick={handleCancelUpdateWithoutCorrection}
              disabled={props.isLoading}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmUpdateWithoutCorrection}
              disabled={props.isLoading}
            >
              <FormattedMessage id="update" />
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default observer(StepFour);
